import React from "react";
import { Link } from "react-router-dom";
import "./About.css";

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--amber)"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const milestones = [
  { year: "2005", event: "Foundation established by Ms. Charity Katotobwe" },
  {
    year: "2008",
    event:
      "First meals served in December — operations begin officially after the tragic loss of a pregnant employee and her baby",
  },
  {
    year: "2009",
    event: "Registered organisation and operations began officially",
  },
  {
    year: "2010",
    event:
      "Nutrition Education Programme launched for pregnant and lactating mothers",
  },
  {
    year: "2012",
    event:
      "Home-Based Care Programme begins — trained caregivers deployed into the community",
  },
  {
    year: "2014",
    event:
      "School Sponsorship Programme expands; first girls sponsored into secondary school",
  },
  {
    year: "2015",
    event:
      "Housing construction begins — first homes built for widows raising orphaned grandchildren",
  },
  {
    year: "2016",
    event:
      "Skills Training Programme launched; bulungu.co.za online store established",
  },
  {
    year: "2018",
    event:
      "Early Childhood Development preschool opens, serving children aged 3–6",
  },
  {
    year: "2022+",
    event:
      "27 houses built; 14,000+ meals served per year; foundation expands to Malawi and South Africa",
  },
];

export default function About() {
  return (
    <main>
      <section className="page-hero" aria-label="About page hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/serious-child-01.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            Our Story
          </span>
          <h1>From Vision to Action</h1>
          <p>
            Bringing real hope — how one woman's conviction turned into 17+
            years of feeding, educating, and caring for the poor and vulnerable
            across Southern Africa.
          </p>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="section" aria-labelledby="founder-heading">
        <div className="container">
          <div className="about-founder-grid">
            <div>
              <span className="eyebrow">The Founder — Executive Director</span>
              <h2 id="founder-heading">Ms. Charity Katotobwe</h2>
              <div className="divider" />
              <p>
                The Food and Nutrition Foundation was not built in a boardroom.
                It was built by a woman who saw the devastating effects of HIV
                and AIDS on women and children from low-income communities — and
                decided to do something about it.
              </p>
              <p>
                Ms. Charity Katotobwe is an Accountant with full ACCA, an MBA,
                and a PhD candidate in Entrepreneurship. She founded the Food
                and Nutrition Foundation in 2005 and started operating in 2008
                when her maid who was pregnant at the time lost her baby and
                then also suddenly died within two months of losing her baby.
                She said enough is enough and decided to do something about it.
              </p>
              <p>
                Her belief is simple and non-negotiable:{" "}
                <strong>
                  "Everyone is entitled to a dignified existence irrespective of
                  socio-economic status."
                </strong>
              </p>
              <p>
                Today, the foundation has grown to two other countries, namely
                Malawi and South Africa, and she continues to lead the
                foundation providing strategic vision and leadership to continue
                the growth and development of impoverished communities.
              </p>
            </div>
            <div className="about-founder-image">
              <img
                src="/images/charity-katotobwe.jpg"
                alt="Ms. Charity Katotobwe, Founder & Executive Director"
              />
              <div className="about-founder-caption">
                <p className="about-founder-name">Ms. Charity Katotobwe</p>
                <p>Founder &amp; Executive Director</p>
                <p>ACCA · MBA · PhD Candidate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section
        className="section mission-section"
        aria-labelledby="mission-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Who We Are</span>
            <h2 id="mission-heading">Mission, Vision &amp; Core Belief</h2>
            <div className="divider divider-center" />
          </div>
          <div className="mission-grid">
            <div className="mission-card">
              <div className="mission-icon-wrap">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--amber)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
              <h3>Our Mission</h3>
              <p>
                To bring disadvantaged communities in urban and rural areas to
                fight hunger and poverty by providing nutrition support,
                education, and skills training.
              </p>
            </div>
            <div className="mission-card">
              <div className="mission-icon-wrap">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--amber)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <h3>Our Vision</h3>
              <p>
                To uplift poor and vulnerable communities and provide a
                dignified existence for all. People are entitled to a dignified
                existence regardless of their social and economic status. Our
                vision is to provide meals to the poor and vulnerable in
                society.
              </p>
            </div>
            <div className="mission-card mission-card-amber">
              <div className="mission-icon-wrap">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="var(--green-dark)"
                  aria-hidden="true"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              </div>
              <h3>Our Core Belief</h3>
              <p
                style={{
                  fontWeight: "600",
                  fontSize: "1.05rem",
                  color: "#fff",
                }}
              >
                "We believe good nutrition begins before birth and results in
                healthy babies who become healthy children and adults who
                contribute positively to society."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HISTORY TIMELINE */}
      <section
        className="section history-section"
        aria-labelledby="history-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Our Journey</span>
            <h2 id="history-heading">From Vision to Action</h2>
            <div className="divider divider-center" />
            <p>
              17+ years of educating and caring for the poor and vulnerable —
              one milestone at a time.
            </p>
          </div>
          <div className="timeline">
            {milestones.map((m, i) => (
              <div className="timeline-item" key={i}>
                <div className="timeline-year">{m.year}</div>
                <div className="timeline-dot" />
                <div className="timeline-content">{m.event}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="section who-section" aria-labelledby="who-heading">
        <div className="container">
          <div className="who-grid">
            <div>
              <span className="eyebrow">Who We Serve</span>
              <h2 id="who-heading">
                The Communities at the Heart of Everything We Do
              </h2>
              <div className="divider" />
              <p>
                The Foundation works with the most vulnerable members of
                communities across Zambia, Malawi and South Africa — people for
                whom systemic support has consistently failed to arrive.
              </p>
              <ul className="who-list">
                {[
                  "Orphaned and vulnerable children who have lost one or both parents",
                  "People living with HIV/AIDS who are too ill to care for themselves",
                  "Youth at risk of dropping out of education or employment",
                  "Vulnerable women, including grandmothers raising multiple orphaned grandchildren alone",
                  "Pregnant and lactating mothers and women with young children without access to nutrition guidance",
                  "Preschool-aged children in communities with no formal ECD provision",
                ].map((item, i) => (
                  <li key={i}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="who-image">
              <img
                src="/images/gallery/children-05.jpg"
                alt="Community members served by the foundation"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band" aria-label="Support CTA">
        <div className="container cta-band-inner">
          <div>
            <h2>Support Our Mission</h2>
            <p>
              Every donation goes directly to the programmes that serve the most
              vulnerable communities across Southern Africa.
            </p>
          </div>
          <div className="cta-band-actions">
            <Link to="/donate" className="btn btn-primary btn-lg">
              Donate Now
            </Link>
            <Link to="/get-involved" className="btn btn-secondary">
              Get Involved
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
